// Meta Pixel helpers. The Pixel itself is loaded by plugins/meta-pixel.client.ts once the
// user accepts cookies; until then every call here is a no-op.

export const CONSENT_COOKIE = "cookie_consent";

export type ConsentValue = "granted" | "denied";

export interface MetaTrackOptions {
  // Use trackCustom instead of track (for events Meta doesn't define, e.g. Login).
  custom?: boolean;
  // Reuse an id that is also sent to the Conversions API so Meta can deduplicate.
  eventId?: string;
}

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

export function useCookieConsent() {
  const cookie = useCookie<ConsentValue | null>(CONSENT_COOKIE, {
    maxAge: 60 * 60 * 24 * 180,
    sameSite: "lax",
    path: "/",
    // Store the raw value so the server can read it without JSON decoding.
    encode: (value) => value ?? "",
    decode: (value) => (value === "granted" || value === "denied" ? value : null),
  });

  // Shared state so the banner, plugin and composable all react to the same value.
  const consent = useState<ConsentValue | null>("cookie-consent", () => cookie.value ?? null);

  function setConsent(value: ConsentValue | null) {
    cookie.value = value;
    consent.value = value;
  }

  return { consent, setConsent };
}

export function createMetaEventId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// The Pixel starts asynchronously (it waits for the logged-in user's data), so events
// fired before it is ready are queued and replayed once it is initialized.
let metaPixelReady = false;
const pendingCalls: any[][] = [];

function callFbq(...args: any[]) {
  if (metaPixelReady && window.fbq) {
    window.fbq(...args);
  } else if (pendingCalls.length < 50) {
    pendingCalls.push(args);
  }
}

export function markMetaPixelReady() {
  metaPixelReady = true;
  pendingCalls.splice(0).forEach((args) => window.fbq?.(...args));
}

export function useMetaPixel() {
  const { consent } = useCookieConsent();
  const { locale } = useI18n();

  // Returns the event id so callers can pass it on to the server-side event.
  function track(event: string, params: Record<string, any> = {}, options: MetaTrackOptions = {}) {
    const eventId = options.eventId ?? createMetaEventId();

    if (import.meta.server || consent.value !== "granted") {
      return eventId;
    }

    callFbq(
      options.custom ? "trackCustom" : "track",
      event,
      { locale: locale.value, ...params },
      { eventID: eventId }
    );

    return eventId;
  }

  return { track };
}
